import { useState } from "react";
import { ChevronDown, CircleDot, SlidersHorizontal, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  FilterOptionRow,
  FilterPanelBody,
  FilterPanelContent,
  FilterPanelHeader,
  FilterSection,
  FilterSections,
} from "@/components/filters/FilterPanel";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { FilterChips } from "@/components/filters/FilterChips";
import { multiValueChip, type FilterChip } from "@/components/filters/filterChip";
import { ISSUE_STATUSES } from "@/domains/pm/constants";
import { getDisciplineName } from "@/domains/pm/utils/disciplineName";
import type { ProtocolIssueFilter } from "@/domains/pm/utils/protocol";
import { cn } from "@/lib/utils";
import type { Language, Translations } from "@/lib/i18n/types";
import type { Discipline, IssueStatus, PMIssue } from "@/domains/pm/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolIssueFiltersProps {
  /** The whole period, so the options describe what is actually here. */
  issues: PMIssue[];
  filter: ProtocolIssueFilter;
  language: Language;
  copy: ProtocolCopy;
  statusLabels: Record<IssueStatus, string>;
  onChange: (filter: ProtocolIssueFilter) => void;
}

function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/**
 * Narrowing the period so a long one can be worked through.
 *
 * One filter panel rather than a control per dimension: the step's own heading row is
 * where this belongs, and two triggers plus a clear button claimed a band of
 * their own for something used once or twice per export.
 *
 * The options come from the loaded issues, not the organisation's catalogue: a
 * discipline nobody in this period used would offer a filter that empties the
 * table, which reads as broken rather than as empty.
 */
export function ProtocolIssueFilters({
  issues,
  filter,
  language,
  copy,
  statusLabels,
  onChange,
}: ProtocolIssueFiltersProps) {
  const { t } = useLanguage();
  const disciplines = new Map<number, Discipline>();
  for (const issue of issues) {
    for (const discipline of issue.disciplines ?? []) disciplines.set(discipline.id, discipline);
  }
  const disciplineOptions = Array.from(disciplines.values()).sort((a, b) =>
    getDisciplineName(a, language).localeCompare(getDisciplineName(b, language), language)
  );
  const presentStatuses = ISSUE_STATUSES.filter((status) =>
    issues.some((issue) => issue.status === status)
  );
  const active = filter.statuses.size + filter.disciplineIds.size;
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string | null>(null);
  // An id with no loaded discipline is left out of the chip's names (never
  // shown as a raw number); the chip's count fallback still covers it.
  const disciplineLabels = [...filter.disciplineIds].flatMap((id) => {
    const d = disciplines.get(id);
    return d ? [getDisciplineName(d, language)] : [];
  });
  const chips: FilterChip<"status" | "discipline">[] = [];
  if (filter.statuses.size > 0) {
    chips.push(
      multiValueChip(
        "status",
        copy.filterStatus,
        [...filter.statuses].flatMap((s) =>
          statusLabels[s] ? [{ id: s, label: statusLabels[s] }] : []
        ),
        () => onChange({ ...filter, statuses: new Set() }),
        { selectedCount: filter.statuses.size }
      )
    );
  }
  if (filter.disciplineIds.size > 0) {
    chips.push(
      multiValueChip(
        "discipline",
        copy.filterDiscipline,
        disciplineLabels,
        () => onChange({ ...filter, disciplineIds: new Set() }),
        { selectedCount: filter.disciplineIds.size }
      )
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
      <FilterChips
        chips={chips}
        onOpenDimension={(d) => {
          setSection(d);
          setOpen(true);
        }}
      />
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSection(null);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("shrink-0 gap-1.5", active > 0 && "border-primary text-primary")}
          >
            <SlidersHorizontal aria-hidden className="h-3.5 w-3.5" />
            {copy.filters}
            {active > 0 && <span className="tabular-nums">({active})</span>}
            <ChevronDown aria-hidden className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <FilterPanelContent align="end">
          <FilterPanelHeader
            title={t.common.filterPanel.active}
            activeCount={active}
            clearLabel={copy.filterClear}
            onClearAll={() => onChange({ statuses: new Set(), disciplineIds: new Set() })}
          />
          <FilterPanelBody>
            <FilterSections value={section} onValueChange={setSection}>
              <FilterSection
                value="status"
                icon={CircleDot}
                label={copy.filterStatus}
                selectedCount={filter.statuses.size}
                onClear={() => onChange({ ...filter, statuses: new Set() })}
                clearLabel={copy.filterClear}
              >
                {presentStatuses.map((status) => (
                  <FilterOptionRow
                    key={status}
                    label={statusLabels[status]}
                    ariaLabel={`${copy.filterStatus}: ${statusLabels[status]}`}
                    checked={filter.statuses.has(status)}
                    onToggle={() =>
                      onChange({ ...filter, statuses: toggled(filter.statuses, status) })
                    }
                  />
                ))}
              </FilterSection>
              {disciplineOptions.length > 0 && (
                <FilterSection
                  value="discipline"
                  icon={Tag}
                  label={copy.filterDiscipline}
                  selectedCount={filter.disciplineIds.size}
                  onClear={() => onChange({ ...filter, disciplineIds: new Set() })}
                  clearLabel={copy.filterClear}
                >
                  {disciplineOptions.map((discipline) => (
                    <FilterOptionRow
                      key={discipline.id}
                      label={getDisciplineName(discipline, language)}
                      ariaLabel={`${copy.filterDiscipline}: ${getDisciplineName(discipline, language)}`}
                      checked={filter.disciplineIds.has(discipline.id)}
                      onToggle={() =>
                        onChange({
                          ...filter,
                          disciplineIds: toggled(filter.disciplineIds, discipline.id),
                        })
                      }
                    />
                  ))}
                </FilterSection>
              )}
            </FilterSections>
          </FilterPanelBody>
        </FilterPanelContent>
      </Popover>
    </div>
  );
}
